// UI registry: domain state -> badge appearance + label. One source of truth so a
// "pending" submission, a "flagged" moment, or a quest capture mode look identical
// everywhere. Replaces the scattered maps (components/badges/chip_maps.js and the
// inline maps that leaked into pages like quests/index `captureModeBadges`).
//
//   badgeFor('submission', 'submitted') -> { label: 'Pending', badgeClass: 'badge-warning' }
//   labelFor('submission', 'submitted') -> 'Pending'
//
// Add a new domain by adding an entry to STATES. Values map to a full DaisyUI badge
// color class. Unknown values fall back to a neutral ghost badge with a humanized label.

const humanize = (value) =>
  String(value ?? '')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())

export const STATES = {
  submission: {
    submitted: { label: 'Pending', badgeClass: 'badge-warning' },
    approved: { label: 'Approved', badgeClass: 'badge-success' },
    rejected: { label: 'Rejected', badgeClass: 'badge-error' }
  },
  friendship: {
    pending: { label: 'Pending', badgeClass: 'badge-warning' },
    accepted: { label: 'Accepted', badgeClass: 'badge-success' },
    declined: { label: 'Declined', badgeClass: 'badge-neutral' }
  },
  media_type: {
    photo: { label: 'Photo', badgeClass: 'badge-info' },
    video: { label: 'Video', badgeClass: 'badge-secondary' }
  },
  moderation: {
    flagged: { label: 'Flagged', badgeClass: 'badge-error' },
    hidden: { label: 'Hidden', badgeClass: 'badge-neutral' },
    reviewed: { label: 'Reviewed', badgeClass: 'badge-success' },
    unreviewed: { label: 'Unreviewed', badgeClass: 'badge-warning' }
  },
  // Raw stored Moment visibility values (Moment#visibility_before_type_cast).
  visibility: {
    public: { label: 'Public', badgeClass: 'badge-success' },
    friends_only: { label: 'Friends only', badgeClass: 'badge-info' },
    private: { label: 'Private', badgeClass: 'badge-ghost' }
  },
  capture_mode: {
    photo: { label: 'Photo', badgeClass: 'badge-info' },
    video: { label: 'Video', badgeClass: 'badge-secondary' },
    text: { label: 'Text', badgeClass: 'badge-accent' }
  },
  // Quest#lifecycle_status — the occurrence's own state, independent of any viewer.
  quest_lifecycle: {
    live: { label: 'Live', badgeClass: 'badge-success' },
    coming_soon: { label: 'Coming Soon', badgeClass: 'badge-info' },
    ended: { label: 'Ended', badgeClass: 'badge-ghost' }
  },
  subscription: {
    trialing: { label: 'Trialing', badgeClass: 'badge-info' },
    active: { label: 'Active', badgeClass: 'badge-success' },
    past_due: { label: 'Past Due', badgeClass: 'badge-warning' },
    canceled: { label: 'Canceled', badgeClass: 'badge-neutral' },
    paused: { label: 'Paused', badgeClass: 'badge-neutral' }
  },
  // Generic on/off used for active flags, published, verified, etc.
  active: {
    true: { label: 'Active', badgeClass: 'badge-success' },
    false: { label: 'Inactive', badgeClass: 'badge-neutral' }
  }
}

export function badgeFor(domain, value) {
  const map = STATES[domain]
  const hit = map?.[String(value)]
  if (hit) return hit
  return { label: humanize(value), badgeClass: 'badge-ghost' }
}

export function labelFor(domain, value) {
  return badgeFor(domain, value).label
}
